import { createContext, useContext, type ReactNode } from "react";
import { createPortal } from "react-dom";

/** Library children place their existing creation actions in the shared heading. */
export const PageHeaderActionsContext = createContext<
  HTMLElement | null | undefined
>(undefined);
import { HelpLink } from "@/features/docs/HelpLink";

/**
 * Per-tab section header for the Coach Workspace list pages.
 *
 * Ports the legacy `admin-panel-head` / `roster-head` pattern
 * (`index.html`): each coach sub-tab card leads with a
 * DESCRIPTIVE title (`<h3>`) + a sub-line explaining the tab,
 * with the page's action buttons on the right.
 *
 * This is distinct from — and sits below — the shell's constant
 * "Coaching Workspace" header. The tab name is never repeated:
 * the strip already shows which tab is active; this header's job
 * is to describe it.
 */

export type PageHeaderProps = {
  /** Descriptive title — e.g. "Review Playlists", not "Playlists". */
  title: string;
  /**
   * Optional mono UPPERCASE eyebrow above the title — the editorial
   * structural label (e.g. "RIVERSIDE FC · SPRING 2026"). Rendered via
   * the shared `.eyebrow` recipe.
   */
  eyebrow?: ReactNode;
  /** One-line explanation of what this tab is for. */
  description: ReactNode;
  /** Optional action buttons, right-aligned on the same row. */
  actions?: ReactNode;
};

export function PageHeader({
  title,
  eyebrow,
  description,
  actions,
}: PageHeaderProps) {
  const actionTarget = useContext(PageHeaderActionsContext);
  if (actionTarget !== undefined) {
    return actionTarget && actions ? createPortal(actions, actionTarget) : null;
  }
  return (
    <div
      className="grid min-w-0 gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start"
      data-testid="coach-page-header"
    >
      <div className="min-w-0 coach-page-heading-copy">
        {eyebrow ? (
          <p className="eyebrow mb-1" data-testid="coach-page-header-eyebrow">
            {eyebrow}
          </p>
        ) : null}
        <h2 className="flex items-center gap-1 font-display text-xl tracking-display text-ink">
          {title}
          <HelpLink className="size-7 coach-heading-help" />
        </h2>
        <p className="mt-0.5 max-w-3xl text-sm leading-snug text-ink-muted">
          {description}
        </p>
      </div>
      {actions ? (
        <div className="min-w-0" data-testid="coach-page-header-actions">
          <div className="flex min-w-0 flex-wrap items-center gap-2 sm:justify-end">
            {actions}
          </div>
        </div>
      ) : null}
    </div>
  );
}
